import { Search } from '@shared/components/ui/Search';
import { SimpleFilter, type SimpleFilterField } from '@shared/components/ui/SimpleFilter';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PayoutItemsFilters } from '../../models';
import { useShopsDropdown } from '../../hooks';
import { ExportButton } from './ExportButton';

interface FiltersSectionProps {
  filters: PayoutItemsFilters;
  onFiltersChange: (newFilters: Partial<PayoutItemsFilters>) => void;
  isFetching?: boolean;
  payoutId: number;
  exportColumns?: string[];
}

export const FiltersSection = ({
  filters,
  onFiltersChange,
  isFetching = false,
  payoutId,
  exportColumns,
}: FiltersSectionProps) => {
  const { t } = useTranslation('codPayoutDetail');
  const [refValue, setRefValue] = useState(filters.ref || '');
  const [trackingValue, setTrackingValue] = useState(filters.tracking_number || '');
  const { shops, isLoading: isLoadingShops } = useShopsDropdown();

  // Sincronizar estados locales con filtros cuando cambian externamente
  useEffect(() => {
    setRefValue(filters.ref || '');
    setTrackingValue(filters.tracking_number || '');
  }, [filters.ref, filters.tracking_number]);

  // Handler para búsqueda de ref (fuera del SimpleFilter)
  const handleRefChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRefValue(e.target.value);
  };

  const handleRefSubmit = useCallback(() => {
    onFiltersChange({ ref: refValue.trim() || undefined });
  }, [refValue, onFiltersChange]);

  // Handler para reset que también limpia el campo de búsqueda
  const handleReset = useCallback(() => {
    setTrackingValue('');
    onFiltersChange({
      tracking_number: undefined,
      shop_id: undefined,
    });
  }, [onFiltersChange]);

  // 🎯 Configuración de campos para SimpleFilter
  const fields: SimpleFilterField[] = useMemo(
    () => [
      {
        key: 'tracking_number',
        label: t('filters.tracking', 'Tracking'),
        type: 'text',
        placeholder: t('filters.tracking_placeholder', 'Buscar por tracking...'),
        textConfig: {
          hasSearchButton: true,
          onSearch: (value: string) => {
            setTrackingValue(value);
            onFiltersChange({ tracking_number: value.trim() || undefined });
          },
        },
        minWidth: '200px',
      },
      {
        key: 'shop_id',
        label: t('filters.shop', 'Tienda'),
        type: 'select',
        options: shops.map((shop) => ({
          label: shop.name,
          value: String(shop.id),
        })),
        allOption: {
          label: t('filters.all_shops', 'Todas las tiendas'),
          value: 'all',
        },
        isLoadingOptions: isLoadingShops,
        placeholder: isLoadingShops
          ? t('filters.loading_shops', 'Cargando...')
          : t('filters.select_shop', 'Seleccionar tienda'),
        minWidth: '180px',
        maxWidth: '220px',
      },
    ],
    [t, shops, isLoadingShops, onFiltersChange],
  );

  // 🎯 Convertir valores de filtros a formato que SimpleFilter espera
  const filterValues = useMemo(() => {
    return {
      tracking_number: trackingValue || undefined,
      shop_id: filters.shop_id ? String(filters.shop_id) : 'all',
    };
  }, [filters.shop_id, trackingValue]);

  // 🎯 Handler para cambios en los filtros del SimpleFilter
  const handleFilterChange = (newValues: Partial<Record<string, any>>) => {
    const updatedFilters: Partial<PayoutItemsFilters> = {};

    // Convertir valores de vuelta al formato del modelo
    Object.keys(newValues).forEach((key) => {
      const value = newValues[key];
      if (key === 'shop_id') {
        updatedFilters.shop_id = value === 'all' || !value ? undefined : parseInt(value, 10);
      } else if (key === 'tracking_number') {
        updatedFilters.tracking_number = value || undefined;
      }
    });

    onFiltersChange(updatedFilters);
  };

  return (
    <div className="mb-4">
      <div className="flex items-center gap-3 flex-wrap">
        {/* Buscador por ref fuera del SimpleFilter */}
        <Search
          placeholder={t('filters.ref_placeholder', 'Buscar por ref...')}
          value={refValue}
          onChange={handleRefChange}
          onEnter={handleRefSubmit}
          dataTestId="search-ref"
          inputDataTestId="search-ref-input"
        />
        {/* SimpleFilter con tracking y shop */}
        <SimpleFilter
          useModal={true}
          fields={fields}
          values={filterValues}
          onChange={handleFilterChange}
          onReset={handleReset}
          isLoading={isFetching}
          loadingText={t('filters.searching', 'Buscando...')}
          clearButtonText={t('filters.clear', 'Limpiar')}
          triggerText={t('filters.title', 'Filtros')}
          translations={{
            filters: t('filters.title', 'Filtros'),
            searching: t('filters.searching', 'Buscando...'),
            clear: t('filters.clear', 'Limpiar'),
          }}
        />
        {exportColumns && <ExportButton payoutId={payoutId} filters={filters} columns={exportColumns} />}
      </div>
    </div>
  );
};
